/**
 * Shared UI text capitalization utility.
 *
 * Implements the design system's **UI Capitalization Style Guide v1.0**
 * (approved June 2026, Paul Knight). Given any UI string and the role of the
 * element it belongs to, {@link formatText} applies the correct casing:
 *
 * - **Title Case** for landmark elements the user navigates by — top/side nav,
 *   breadcrumbs, headers, tabs, menus, modal titles, table column headers, card
 *   titles.
 * - **Sentence case** for short single-sentence copy — buttons, input labels,
 *   placeholders, checkbox/radio labels. Only the first word is capitalized.
 * - **Body case** for multi-sentence copy — descriptions, hints, errors, tooltips,
 *   alert bodies. Same as Sentence case, but every new sentence (after `.`, `!`,
 *   `?`) is capitalized too. Numbered-list markers (`1.`), ellipses (`...`) and
 *   {@link SENTENCE_ABBREVIATIONS} do not start a sentence. A decimal or price only
 *   ends one when it genuinely closes the sentence (`costs $25.00. Register now.`);
 *   mid-sentence, `3.5` has no terminator to begin with.
 *
 * Prefer this over hand-casing strings so casing stays consistent across every
 * frontend. The function is pure and has no runtime dependencies.
 *
 * @see UI Capitalization Style Guide v1.0
 */

const TITLE_CASE = 'TITLE_CASE';
const SENTENCE_CASE = 'SENTENCE_CASE';
const BODY_CASE = 'BODY_CASE';

/**
 * The role of the UI element a string is rendered in. The role — not the caller —
 * decides whether the string is Title Cased or Sentence cased.
 *
 * For breadcrumbs, format each crumb's text individually; do not pass the joined
 * `Home > My Account` string.
 */
export enum TextRole {
  // Title Case roles (landmark elements)
  TopNavigation = 'topNavigation',
  SideNavigation = 'sideNavigation',
  Breadcrumb = 'breadcrumb',
  PageHeader = 'pageHeader', // H1
  SectionHeader = 'sectionHeader', // H2/H3
  TabLabel = 'tabLabel',
  ModalTitle = 'modalTitle',
  MenuItem = 'menuItem',
  TableColumnHeader = 'tableColumnHeader',
  CardTitle = 'cardTitle',
  // Sentence case roles (short, single-sentence supporting text)
  ButtonLabel = 'buttonLabel',
  InputLabel = 'inputLabel',
  Placeholder = 'placeholder',
  CheckboxLabel = 'checkboxLabel',
  RadioLabel = 'radioLabel',
  // Body case roles (supporting copy that can run to several sentences)
  HintText = 'hintText', // also helper text
  ErrorMessage = 'errorMessage',
  Tooltip = 'tooltip',
  AlertBody = 'alertBody', // notification/alert body
  DescriptiveText = 'descriptiveText',
}

/**
 * Maps each {@link TextRole} to the casing rule it uses: Title Case for landmark
 * elements, Sentence case for single-sentence labels, Body case for copy that can
 * contain several sentences.
 */
const TEXT_ROLE: Record<TextRole, string> = {
  [TextRole.TopNavigation]: TITLE_CASE,
  [TextRole.SideNavigation]: TITLE_CASE,
  [TextRole.Breadcrumb]: TITLE_CASE,
  [TextRole.PageHeader]: TITLE_CASE,
  [TextRole.SectionHeader]: TITLE_CASE,
  [TextRole.TabLabel]: TITLE_CASE,
  [TextRole.ModalTitle]: TITLE_CASE,
  [TextRole.MenuItem]: TITLE_CASE,
  [TextRole.TableColumnHeader]: TITLE_CASE,
  [TextRole.CardTitle]: TITLE_CASE,
  [TextRole.ButtonLabel]: SENTENCE_CASE,
  [TextRole.InputLabel]: SENTENCE_CASE,
  [TextRole.Placeholder]: SENTENCE_CASE,
  [TextRole.CheckboxLabel]: SENTENCE_CASE,
  [TextRole.RadioLabel]: SENTENCE_CASE,
  [TextRole.HintText]: BODY_CASE,
  [TextRole.ErrorMessage]: BODY_CASE,
  [TextRole.Tooltip]: BODY_CASE,
  [TextRole.AlertBody]: BODY_CASE,
  [TextRole.DescriptiveText]: BODY_CASE,
};

/**
 * Title Case "minor" words — fully lowercased unless they are the first or last
 * word of the text. Articles, coordinating conjunctions, and short prepositions.
 * Exported so teams can inspect or extend the set.
 */
export const TITLE_CASE_MINOR_WORDS = new Set<string>([
  'a',
  'an',
  'the',
  'and',
  'but',
  'or',
  'nor',
  'for',
  'so',
  'yet',
  'at',
  'by',
  'in',
  'of',
  'on',
  'to',
  'up',
]);

/**
 * Words and phrases emitted in their canonical form under any casing rule. Matching
 * is case-insensitive and ignores leading/trailing punctuation; the canonical
 * spelling in this set is what gets rendered (`url` → `URL`, `paypal` → `PayPal`).
 *
 * An entry containing a space is matched as a **phrase** across consecutive words,
 * and its internal capitalization is what wins — `king of the court` renders as
 * `King of the Court` in every role, minor words included. The final word of a
 * phrase may carry a plural `s`/`es` (`rating sessions` → `Rating Sessions`);
 * single-word entries are matched exactly, so `aches` never becomes `ACHes`.
 *
 * Acronyms keep their conventional form and proper nouns are always capitalized
 * (per the style guide). There is no auto-detection beyond this set — extend it
 * for your own brands/acronyms (`PRESERVED_WORDS.add('MyBrand')`) or pass
 * `override: true` for a one-off proper noun the function can't know about.
 */
export const PRESERVED_WORDS = new Set<string>([
  // Style-guide seed.
  'URL',
  'ID',
  'PDF',
  'API',
  'Google',
  'Pickleball',
  'Pickleball.com',
  'PickleballTournaments.com',
  'PickleballClubs',
  'PickleballLeagues',
  'Pickles',
  'AI',
  'RSVPs',
  'PickleballGameMaker.com',
  'GameMaker',
  'APR',
  'TS',
  'BS',
  'PT',
  'POP',
  // Platform acronyms and brands, kept so existing UI copy doesn't regress.
  'FAQ',
  'USA',
  'ZIP',
  'ACH',
  'SKU',
  'CC',
  'PWID',
  'DUPR',
  'TBD',
  'SMS',
  'TV',
  'UTC',
  'MLP',
  'PTPerks',
  // Clock suffixes. Body/sentence case would otherwise lowercase them mid-string, so any copy that
  // interpolates a formatted time ("outside the 7:00 AM - midnight window") came back mangled.
  'AM',
  'PM',
  'KOTC',
  'PPA',
  'Apple',
  'PayPal',
  'Stripe',
  'DailyStory',
  'YouTube',
  'Facebook',
  'T/O',
  'Venmo',
  'iPhone',
  'Android',
  // Product vocabulary. Entries with a space are matched as phrases.
  'Major League Pickleball',
  'Percentage of Points',
  'National Sponsor Network',
  'Tournament Strength',
  'Bracket Strength',
  'King of the Court',
  'Rating Session',
  'Skinny Singles',
  'Club Rating',
  'Coed/Any',
  'Pickleball Central',
  'Up/Down',
  // Slash compounds are a single token, so the half after the slash would otherwise be lowercased.
  'Self-Rating',
  'Sanctioned/Approved',
  'Show/Hide',
  'Zip/Postal',
  'Round-Robin',
  'Single Elim. Bracket',
  'Double Elim. Bracket',
  'RR',
  'MCP',
  'MTok',
  'LLM',
  'USD',
]);

/**
 * Abbreviations whose trailing period does **not** end a sentence, so the word
 * after them is not capitalized in Body case. Matched case-insensitively against
 * the whole token. Exported so teams can extend the set
 * (`SENTENCE_ABBREVIATIONS.add('dept.')`).
 *
 * Only unambiguous abbreviations belong here. `etc.` is deliberately absent — it
 * usually does close a sentence, and so do `no.`, `min.`, `max.` and `est.` in UI
 * copy ("the answer is no.", "set the max."), which is why they are left out too.
 */
export const SENTENCE_ABBREVIATIONS = new Set<string>([
  'e.g.',
  'i.e.',
  'vs.',
  'approx.',
  'mr.',
  'mrs.',
  'ms.',
  'dr.',
  'st.',
  'inc.',
  'ltd.',
  'jr.',
  'sr.',
  'elim.',
]);

export interface FormatTextProps {
  /** The raw UI string to format. */
  text: string;
  /** The role of the element the text is rendered in; selects the casing rule. */
  role: TextRole;
  /**
   * When `true`, return `text` unchanged. Escape hatch for proper nouns the
   * function can't know about, localized (non-English) strings, and approved
   * exceptions. Defaults to `false`.
   */
  override?: boolean;
}

/** Strip leading and trailing punctuation, leaving letters/digits (and any internal punctuation). */
const bareWord = (token: string): string =>
  token.replace(/^[^A-Za-z0-9]+/, '').replace(/[^A-Za-z0-9]+$/, '');

/**
 * A preserved word can carry an inflectional suffix that punctuation hides from
 * {@link bareWord}: `ID(s)` bares to `ID(s`, and `IDs` is a different token than `ID`.
 * Returns the candidates to try against the preserve map, longest first so an entry
 * for the exact inflected form (`RSVPs`) still wins over its stem.
 */
const preserveCandidates = (bare: string): string[] => {
  const candidates = [bare];

  // `ID(s` -> `ID`, from a token written `ID(s)`.
  const parenthesised = bare.match(/^([A-Za-z0-9]+)\([sS]$/);
  if (parenthesised?.[1]) candidates.push(parenthesised[1]);

  // `IDs` -> `ID`, and `ids`/`IDS` alike: the trailing plural is stripped case-insensitively so a
  // miscased token still resolves. Whether the stem is really an acronym is decided by the caller,
  // which accepts the candidate only when its canonical spelling is all-caps — so ordinary plurals
  // ("courts", "matches") never match an entry like `Court`.
  const pluralStem = bare.match(/^([A-Za-z0-9]{2,})[sS]$/);
  if (pluralStem?.[1]) candidates.push(pluralStem[1]);

  return candidates;
};

/** Whether a token contains at least one letter or digit (i.e. is a "word", not empty/pure punctuation). */
const hasAlphaNumeric = (token: string): boolean => /[A-Za-z0-9]/.test(token);

/** Whether a token opens with punctuation, as `(see` and `"try` do. */
const hasLeadingPunctuation = (token: string): boolean => /^[^A-Za-z0-9]/.test(token);

/** Whether a token closes with punctuation, as `club,` and `court."` do. */
const hasTrailingPunctuation = (token: string): boolean => /[^A-Za-z0-9]$/.test(token);

/**
 * Strip surrounding quotes and brackets, keeping any internal or trailing period so
 * an abbreviation stays intact: `(e.g.` → `e.g.`. Unlike {@link bareWord} this is safe
 * for {@link SENTENCE_ABBREVIATIONS} lookups, which need that final dot.
 */
const unwrapToken = (token: string): string =>
  token.replace(/^["'“‘([{]+/, '').replace(/["'’”)\]}]+$/, '');

/**
 * Split text into alternating parts: even indexes are tokens, odd indexes are the
 * whitespace runs between them. Re-joining with `join('')` round-trips the original
 * whitespace — spaces, tabs and newlines alike — exactly.
 */
const splitParts = (text: string): string[] => text.split(/(\s+)/);

/** A part that is a word: a token, not a separator, holding at least one letter or digit. */
interface WordPart {
  /** Index of this word in the parts array it came from. */
  partIndex: number;
  token: string;
}

/** The words of a parts array, in order, with their part indexes. */
const wordParts = (parts: string[]): WordPart[] =>
  parts.reduce<WordPart[]>((words, part, i) => {
    if (i % 2 === 0 && hasAlphaNumeric(part)) words.push({ partIndex: i, token: part });

    return words;
  }, []);

/** Swap a token's word for `canonical`, keeping the punctuation that surrounds it. */
const withSurroundingPunctuation = (token: string, canonical: string): string => {
  const leading = token.match(/^[^A-Za-z0-9]*/)?.[0] ?? '';
  const trailing = token.match(/[^A-Za-z0-9]*$/)?.[0] ?? '';

  const canonicalTrailing = canonical.match(/[^A-Za-z0-9]*$/)?.[0] ?? '';

  return `${leading}${canonical}${canonicalTrailing ? '' : trailing}`;
};

let preserveMapCache: Map<string, string> | null = null;
let preserveMapCacheSize = -1;

/**
 * Build a lookup from an upper-cased bare word to its canonical spelling, honoring
 * runtime additions. Multi-word entries are skipped — they are phrase-matched
 * instead, and could never match a single token.
 *
 * `formatText` runs on every rendered label, so the result is cached and rebuilt only
 * when {@link PRESERVED_WORDS} grows or shrinks — keeping the documented
 * `PRESERVED_WORDS.add('MyBrand')` escape hatch working. Swapping one entry for another
 * between calls leaves the size unchanged and so goes unnoticed; extend the set at
 * module load, not mid-render, and that never comes up.
 */
const buildPreserveMap = (): Map<string, string> => {
  if (preserveMapCache && preserveMapCacheSize === PRESERVED_WORDS.size) return preserveMapCache;

  const map = new Map<string, string>();
  for (const word of PRESERVED_WORDS) {
    if (!/\s/.test(word)) map.set(word.toUpperCase(), word);
  }

  preserveMapCache = map;
  preserveMapCacheSize = PRESERVED_WORDS.size;

  return map;
};

/** Canonical spelling for a preserved token (with its surrounding punctuation kept), or `null`. */
const preservedToken = (token: string, preserveMap: Map<string, string>): string | null => {
  const bare = bareWord(token);
  if (!bare) return null;

  const [exact, ...derived] = preserveCandidates(bare);

  for (const candidate of [exact as string, ...derived]) {
    const canonical = preserveMap.get(candidate.toUpperCase());
    if (!canonical) continue;

    // A derived candidate dropped a plural or a `(s` from the token, so it is only trusted when the
    // stem is genuinely an acronym — otherwise "courts" would resolve against an entry like `Court`
    // and lose its plural. The token as written is always allowed to match.
    if (candidate !== exact && canonical !== canonical.toUpperCase()) continue;

    // Re-attach whatever the candidate dropped (`s` from `IDs`) so the token keeps its
    // own inflection while the stem takes its canonical casing.
    const suffix = bare.slice(candidate.length);

    return withSurroundingPunctuation(token, `${canonical}${suffix}`);
  }

  return null;
};

/**
 * Capitalize a token's first character (only if it's a letter) and lowercase the
 * rest. If the token starts with a digit or punctuation the leading capitalization
 * is a no-op, but the remainder is still lowercased.
 */
const capitalize = (token: string): string => {
  if (!token) return token;
  const first = token.charAt(0);
  const rest = token.slice(1).toLowerCase();
  const head = /[A-Za-z]/.test(first) ? first.toUpperCase() : first.toLowerCase();

  return `${head}${rest}`;
};

/**
 * Capitalize the first *letter* in a token, skipping any leading non-letter
 * characters, and lowercase the rest. Unlike {@link capitalize} it looks past a
 * leading symbol, so `#season` becomes `#Season`.
 */
const capitalizeFirstLetter = (token: string): string => {
  const index = token.search(/[A-Za-z]/);
  if (index < 0) return token;

  const head = token.slice(0, index);
  const letter = token.charAt(index).toUpperCase();
  const rest = token.slice(index + 1).toLowerCase();

  return `${head}${letter}${rest}`;
};

/** Opening quotes and brackets that can precede the first word of a sentence. */
const SENTENCE_OPENERS = /^["'“‘([{]+/;

/**
 * Capitalize the word that opens a sentence, looking past an opening quote or bracket
 * so `"try` and `(see` are capitalized.
 *
 * Only those wrappers are skipped — deliberately not every non-letter, which would
 * reach into tokens that merely begin with a symbol or digit and raise a letter mid-word
 * (`/month` → `/Month`, `3rd` → `3Rd`).
 */
const capitalizeSentenceStart = (token: string): string => {
  const opener = token.match(SENTENCE_OPENERS)?.[0] ?? '';

  return `${opener}${capitalize(token.slice(opener.length))}`;
};

/**
 * Whether a token is a "#" (number-of) label such as `#Season`. The leading hash
 * reads as "number of", so the word after it stays capitalized under every casing
 * rule rather than being lowercased as a minor word or in sentence case.
 */
const isNumberSignLabel = (token: string): boolean => /^#[A-Za-z]/.test(token);

/**
 * Whether a token is a literal a user is expected to read character-for-character rather than prose:
 * an email address, a URL, or a domain. Placeholders like `joe.doe@gmail.com` are samples of what to
 * type, so capitalizing them ("Joe.doe@gmail.com") misrepresents the value. Left exactly as authored
 * under every casing rule.
 */
const VERBATIM_LITERAL =
  /^["'“‘([{]*(?:[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}|(?:https?:\/\/|www\.)[^\s]+|[A-Za-z0-9-]+\.(?:com|net|org|io|co|gov|edu)\b[^\s]*)["'’”)\]}]*[.,;:!?]?$/;

const isVerbatimLiteral = (token: string): boolean => VERBATIM_LITERAL.test(token);

/**
 * A lone `#`, as in `# of rounds`. It carries no letters, so the casing loops skip it —
 * but it does open the text, and the word after it ("of") must not be treated as the
 * sentence start or it capitalizes to `# Of rounds`.
 */
const isBareNumberSign = (token: string): boolean => /^#+$/.test(token);

/** A sentence terminator, optionally wrapped up by closing quotes/brackets: `end."`, `(end!)`. */
const SENTENCE_TERMINATOR = /[.!?]+["'’”)\]}]*$/;
/** `...` is a pause, not a sentence end. */
const ELLIPSIS = /\.\.+["'’”)\]}]*$/;
/**
 * A whole-token numbered-list marker, whose period never ends a sentence: `1.`, `18.`,
 * `(1.)`. Deliberately narrow — it matches a bare integer and a period, so a price or
 * decimal that ends a sentence (`$25.00.`, `up to 3.5.`) still opens the next one, and
 * `!`/`?` after a digit (`error code 5!`) is always a sentence end since no list uses them.
 */
const LIST_MARKER = /^["'“‘([{]*\d+\.["'’”)\]}]*$/;
/** Letter-by-letter initialisms whose final dot is part of the abbreviation: `U.S.`, `a.m.`. */
const DOTTED_INITIALISM = /^(?:[A-Za-z]\.){2,}["'’”)\]}]*$/;

/**
 * Whether a token closes a sentence, so the next word starts a new one. Excludes
 * ellipses, numbered-list markers and numbers, dotted initialisms, and the known
 * {@link SENTENCE_ABBREVIATIONS}.
 */
const endsSentence = (token: string): boolean => {
  if (!SENTENCE_TERMINATOR.test(token)) return false;

  // Both the initialism and abbreviation checks are anchored, so they run on the
  // unwrapped token — `(u.s.` and `(e.g.` are the same abbreviations as bare ones.
  const unwrapped = unwrapToken(token);
  if (ELLIPSIS.test(token) || LIST_MARKER.test(token) || DOTTED_INITIALISM.test(unwrapped)) {
    return false;
  }

  return !SENTENCE_ABBREVIATIONS.has(unwrapped.toLowerCase());
};

interface PreservedPhrase {
  /** The phrase's words, lowercased, for matching. */
  words: string[];
  /** The phrase's words in their canonical spelling, for output. */
  canonical: string[];
}

interface PhraseIndex {
  /** The phrases themselves, longest first. */
  phrases: PreservedPhrase[];
  /** Every word that can open a phrase, so hopeless positions are skipped cheaply. */
  firstWords: Set<string>;
}

let phraseIndexCache: PhraseIndex | null = null;
let phraseIndexCacheSize = -1;

/**
 * Index the multi-word entries of {@link PRESERVED_WORDS}, longest first so a longer
 * phrase always wins over a shorter one that starts at the same word.
 *
 * Cached on {@link PRESERVED_WORDS} size like {@link buildPreserveMap}, since the
 * filter/split/sort is far too much work to repeat per rendered label.
 */
const buildPhraseIndex = (): PhraseIndex => {
  if (phraseIndexCache && phraseIndexCacheSize === PRESERVED_WORDS.size) return phraseIndexCache;

  const phrases = [...PRESERVED_WORDS]
    .filter((entry) => /\s/.test(entry))
    .map((entry) => {
      const canonical = entry.trim().split(/\s+/);

      return { words: canonical.map((word) => word.toLowerCase()), canonical };
    })
    .sort((a, b) => b.words.length - a.words.length);

  phraseIndexCache = {
    phrases,
    firstWords: new Set(phrases.map((phrase) => phrase.words[0] ?? '')),
  };
  phraseIndexCacheSize = PRESERVED_WORDS.size;

  return phraseIndexCache;
};

/** Words that pluralize with `es` rather than `s`: sibilant endings (`match` → `matches`). */
const TAKES_ES_PLURAL = /(?:[sxz]|ch|sh)$/;

/**
 * A plural suffix the final word of a phrase may carry: `Rating Session` → `Rating Sessions`.
 * `es` is only accepted where English would use it, so `Rating Sessiones` is not a match.
 */
const pluralSuffix = (bare: string, phraseWord: string): string | null => {
  if (bare === phraseWord) return '';
  if (bare === `${phraseWord}s`) return bare.slice(phraseWord.length);
  if (TAKES_ES_PLURAL.test(phraseWord) && bare === `${phraseWord}es`) {
    return bare.slice(phraseWord.length);
  }

  return null;
};

/**
 * Try to match a preserved phrase starting at word position `at`. On a match, returns
 * how many words it spans and the canonical replacement for each of their parts;
 * otherwise `null`.
 *
 * A phrase's words must be genuinely adjacent, separated by nothing but whitespace.
 * Anything between them — `club, rating`, `club / rating`, `club — rating`, or a
 * sentence break in `the club. rating starts` — means these are ordinary words that
 * happen to sit near each other, not the phrase. Only the phrase's outer edges may
 * carry punctuation, so a quoted `"king of the court"` still matches.
 */
const matchPhraseAt = (
  words: WordPart[],
  at: number,
  phrases: PreservedPhrase[]
): { length: number; replacements: Map<number, string> } | null => {
  for (const phrase of phrases) {
    const { length } = phrase.words;
    if (at + length > words.length) continue;

    const replacements = new Map<number, string>();
    let matched = true;

    for (let k = 0; k < length; k += 1) {
      const word = words[at + k];
      const previous = k > 0 ? words[at + k - 1] : undefined;
      if (!word) {
        matched = false;
        break;
      }

      const { partIndex, token } = word;
      const phraseWord = bareWord(phrase.words[k] ?? '').toLowerCase();
      const canonicalWord = phrase.canonical[k] ?? '';
      const bare = bareWord(token).toLowerCase();

      // Words are two parts apart when only whitespace separates them; a punctuation-only
      // part in between (`club / rating`) pushes them further apart.
      const isAdjacent = !previous || partIndex === previous.partIndex + 2;
      const tokenTrailingPunct = token.match(/[^A-Za-z0-9]*$/)?.[0] ?? '';
      const canonicalTrailingPunct = canonicalWord.match(/[^A-Za-z0-9]*$/)?.[0] ?? '';

      const isInnerEdgeClean =
        (k === 0 || !hasLeadingPunctuation(token)) &&
        (k === length - 1 ||
          !hasTrailingPunctuation(token) ||
          tokenTrailingPunct === canonicalTrailingPunct);

      if (!isAdjacent || !isInnerEdgeClean) {
        matched = false;
        break;
      }

      // Every word but the last must match exactly; the last may carry a plural suffix,
      // which is re-emitted as typed.
      const suffix = k === length - 1 ? pluralSuffix(bare, phraseWord) : '';
      if (suffix === null || (k < length - 1 && bare !== phraseWord)) {
        matched = false;
        break;
      }

      replacements.set(partIndex, withSurroundingPunctuation(token, `${canonicalWord}${suffix}`));
    }

    if (matched) return { length, replacements };
  }

  return null;
};

/**
 * Canonical replacements for every preserved phrase in `words`, keyed by part index.
 * Phrases are matched left to right and never overlap.
 */
const buildPhraseFixes = (words: WordPart[]): Map<number, string> => {
  const { phrases, firstWords } = buildPhraseIndex();
  const fixes = new Map<number, string>();
  if (!phrases.length) return fixes;

  for (let at = 0; at < words.length; ) {
    // Most words can't open any phrase; a set lookup skips them before the (much more
    // expensive) per-phrase walk.
    const word = words[at];
    if (!word || !firstWords.has(bareWord(word.token).toLowerCase())) {
      at += 1;
      continue;
    }

    const match = matchPhraseAt(words, at, phrases);
    if (!match) {
      at += 1;
      continue;
    }

    for (const [partIndex, replacement] of match.replacements) fixes.set(partIndex, replacement);
    at += match.length;
  }

  return fixes;
};

/**
 * Shared implementation for Sentence case and Body case. Both lowercase every word
 * except the ones that start a sentence; `multiSentence` (Body case) additionally
 * treats each {@link endsSentence} token as opening a new sentence, while Sentence
 * case only ever capitalizes the first word.
 *
 * A word belonging to a preserved phrase is emitted from `phraseFixes` as-is, and
 * still counts as opening (and possibly closing) a sentence.
 */
const formatSentenceCase = (
  parts: string[],
  preserveMap: Map<string, string>,
  phraseFixes: Map<number, string>,
  multiSentence: boolean
): string => {
  let atSentenceStart = true;

  return parts
    .map((part, i) => {
      // Odd indexes are the captured whitespace separators; pure punctuation neither
      // starts nor ends a sentence. A lone `#` is the exception: it consumes the sentence
      // start so the word after it is not capitalized (`# of rounds`, not `# Of rounds`).
      if (i % 2 === 1 || !hasAlphaNumeric(part)) {
        if (isBareNumberSign(part)) atSentenceStart = false;

        return part;
      }

      const phrase = phraseFixes.get(i);
      const preserved = phrase ?? preservedToken(part, preserveMap);
      const startsHere = atSentenceStart;
      atSentenceStart = multiSentence && endsSentence(part);

      // Emails, URLs and domains are values to be typed verbatim, not prose to be cased. A canonical
      // spelling from PRESERVED_WORDS still wins, so `pickleball.com` normalizes as it does elsewhere.
      if (isVerbatimLiteral(part)) return preserved ?? part;

      // "#Season" (number-of) labels stay capitalized wherever they appear.
      if (isNumberSignLabel(part)) return preserved ?? capitalizeFirstLetter(part);

      // A sentence can open on punctuation — `"try again"`, `(see settings)` — so look
      // past it for the first letter rather than only at character 0.
      if (startsHere) return preserved ?? capitalizeSentenceStart(part);

      return preserved ?? part.toLowerCase();
    })
    .join('');
};

/**
 * Format a UI string for the element `role` it belongs to, applying the casing
 * the style guide assigns to that role.
 *
 * Whitespace round-trips exactly: the text is split on whitespace runs, which are
 * re-emitted verbatim, so leading, trailing and consecutive spaces, tabs and newlines
 * are all preserved. An empty string returns an empty string.
 *
 * @example
 * formatText({ text: 'save changes', role: TextRole.ButtonLabel }); // 'Save changes'
 * formatText({ text: 'date joined', role: TextRole.TableColumnHeader }); // 'Date Joined'
 * formatText({ text: 'join the king of the court', role: TextRole.ButtonLabel });
 * // 'Join the King of the Court'
 * formatText({ text: 'removes the player. this cannot be undone.', role: TextRole.DescriptiveText });
 * // 'Removes the player. This cannot be undone.'
 * formatText({ text: 'steps: 1. pick a court', role: TextRole.DescriptiveText }); // 'Steps: 1. pick a court'
 * formatText({ text: 'iPhone Storage Full', role: TextRole.AlertBody, override: true }); // 'iPhone Storage Full'
 *
 * @see UI Capitalization Style Guide v1.0
 */
export function formatText({ text, role, override = false }: FormatTextProps): string {
  if (override) return text;
  if (!text) return text;

  const preserveMap = buildPreserveMap();
  const parts = splitParts(text);
  const words = wordParts(parts);
  const phraseFixes = buildPhraseFixes(words);

  if (TEXT_ROLE[role] === TITLE_CASE) {
    const firstWord = words[0]?.partIndex;
    const lastWord = words[words.length - 1]?.partIndex;

    return parts
      .map((part, i) => {
        if (i % 2 === 1 || !hasAlphaNumeric(part)) return part;

        const preserved = phraseFixes.get(i) ?? preservedToken(part, preserveMap);
        if (preserved) return preserved;

        // Emails, URLs and domains are values to be typed verbatim, not prose to be cased.
        if (isVerbatimLiteral(part)) return part;

        if (isNumberSignLabel(part)) return capitalizeFirstLetter(part);

        const bare = bareWord(part).toLowerCase();
        const isEdge = i === firstWord || i === lastWord;
        if (!isEdge && TITLE_CASE_MINOR_WORDS.has(bare)) return part.toLowerCase();

        return capitalize(part);
      })
      .join('');
  }

  // Sentence case capitalizes only the first word; Body case also capitalizes every
  // new sentence. Preserved words and phrases stay canonical under both.
  return formatSentenceCase(parts, preserveMap, phraseFixes, TEXT_ROLE[role] === BODY_CASE);
}
